/**
 * Configuración → Métodos de pago · Detracción (`/configuracion/metodos-pago-detraccion`).
 *
 * Puerto de `Metodo_pago_detraccion` + `views/metodo_pago_detraccion/index.php`:
 * el catálogo de códigos de medio de pago que exige SUNAT en las operaciones
 * sujetas a detracción (001 Depósito en cuenta, 022 Otros medios de pago…).
 *
 * Diferencias anotadas: el código no puede repetirse (409, la tabla no tiene
 * índice único), **editar no reactiva** (antes `estado` se forzaba a 1 en cada
 * guardado) y no hay borrado: se desactiva.
 */

import { ArrowLeft, Banknote, Pencil, Plus, Power, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';

import { useMetodosDetraccion, useMutarMetodosDetraccion } from '../catalogos.api';
import type { MetodoDetraccion } from '../catalogos.types';

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

/** Alta/edición de un método de pago de detracción. */
function MetodoDialogo({
  metodo,
  guardando,
  error,
  onGuardar,
  onCerrar,
}: {
  metodo: MetodoDetraccion | null;
  guardando: boolean;
  error: string | null;
  onGuardar: (payload: { codigo: string; descripcion: string }, activo?: boolean) => void;
  onCerrar: () => void;
}) {
  const [codigo, setCodigo] = useState(metodo?.codigo ?? '');
  const [descripcion, setDescripcion] = useState(metodo?.descripcion ?? '');
  const [activo, setActivo] = useState(metodo?.activo ?? true);

  const valido = codigo.trim().length > 0 && descripcion.trim().length > 0;

  return (
    <Dialog
      open
      onClose={onCerrar}
      title={metodo ? `Editar medio ${metodo.codigo}` : 'Nuevo medio de pago'}
      description="Códigos del catálogo SUNAT de medios de pago para detracciones."
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={guardando}
            disabled={!valido}
            onClick={() =>
              onGuardar(
                { codigo: codigo.trim(), descripcion: descripcion.trim() },
                metodo ? activo : undefined,
              )
            }
          >
            Guardar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? <Alert variant="error" title="No se pudo guardar">{error}</Alert> : null}

        <Input
          label="Código"
          value={codigo}
          maxLength={20}
          autoFocus={!metodo}
          placeholder="Ej. 001"
          onChange={(evento) => setCodigo(evento.target.value)}
        />

        <div className="flex flex-col gap-1.5">
          <label htmlFor="detraccion-descripcion" className="text-sm font-medium text-ink-700">
            Descripción
          </label>
          <textarea
            id="detraccion-descripcion"
            className="min-h-20 w-full rounded-control border border-ink-200 bg-white px-3 py-2 text-sm text-ink-800 transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none"
            value={descripcion}
            placeholder="Descripción oficial del medio de pago…"
            onChange={(evento) => setDescripcion(evento.target.value)}
          />
        </div>

        {metodo ? (
          <Checkbox
            label="Medio activo"
            hint="Sólo los activos se ofrecen al registrar una detracción"
            checked={activo}
            onChange={(evento) => setActivo(evento.target.checked)}
          />
        ) : null}
      </div>
    </Dialog>
  );
}

export function MetodosPagoDetraccionPage() {
  useDocumentTitle('Configuración · Métodos de pago de detracción');

  const metodos = useMetodosDetraccion();
  const mutaciones = useMutarMetodosDetraccion();

  const [dialogo, setDialogo] = useState<{ abierto: boolean; metodo: MetodoDetraccion | null }>({
    abierto: false,
    metodo: null,
  });
  const [aviso, setAviso] = useState<{ tono: 'success' | 'error'; texto: string } | null>(null);
  const [errorDialogo, setErrorDialogo] = useState<string | null>(null);

  const sinPermiso = metodos.error instanceof ApiError && metodos.error.status === 403;
  const lista = metodos.data?.metodos ?? [];

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/configuracion"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Configuración
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <Banknote className="size-6 text-brand-500" aria-hidden="true" />
          Métodos de pago · Detracción
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Medios de pago del catálogo SUNAT que se declaran en las operaciones sujetas a detracción.
        </p>
      </div>

      <Button
        size="sm"
        leftIcon={<Plus className="size-3.5" aria-hidden="true" />}
        disabled={sinPermiso}
        onClick={() => {
          setErrorDialogo(null);
          setDialogo({ abierto: true, metodo: null });
        }}
      >
        Nuevo medio
      </Button>
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            Esta pantalla es del grupo <strong>Configuración</strong>.
          </span>
        </Alert>
      </div>
    );
  }

  const guardar = (payload: { codigo: string; descripcion: string }, activo?: boolean) => {
    setErrorDialogo(null);
    const alGuardar = {
      onSuccess: (datos: { mensaje: string }) => {
        setDialogo({ abierto: false, metodo: null });
        setAviso({ tono: 'success' as const, texto: datos.mensaje });
      },
      onError: (error: unknown) =>
        setErrorDialogo(mensajeDe(error, 'No se pudo guardar el medio de pago.')),
    };

    if (dialogo.metodo) {
      mutaciones.actualizar.mutate(
        { id: dialogo.metodo.id, payload: { ...payload, ...(activo === undefined ? {} : { activo }) } },
        alGuardar,
      );
      return;
    }

    mutaciones.crear.mutate(payload, alGuardar);
  };

  const cambiarEstado = (metodo: MetodoDetraccion) => {
    mutaciones.cambiarEstado.mutate(
      { id: metodo.id, activo: !metodo.activo },
      {
        onSuccess: (datos) => setAviso({ tono: 'success', texto: datos.mensaje }),
        onError: (error) =>
          setAviso({ tono: 'error', texto: mensajeDe(error, 'No se pudo cambiar el estado.') }),
      },
    );
  };

  return (
    <div className="space-y-6">
      {cabecera}

      {aviso ? (
        <Alert variant={aviso.tono === 'success' ? 'success' : 'error'}>{aviso.texto}</Alert>
      ) : null}

      {metodos.isLoading ? (
        <Skeleton className="h-80 w-full" />
      ) : metodos.error && !sinPermiso ? (
        <Alert variant="error" title="No se pudieron leer los medios de pago">
          {mensajeDe(metodos.error, 'La API no respondió.')}
        </Alert>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Catálogo de medios de pago</CardTitle>
            <Badge variant="neutral">{metodos.data?.total ?? 0} registrados</Badge>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[40rem] border-collapse text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                    <th className="w-24 pb-2">Código</th>
                    <th className="pb-2">Descripción</th>
                    <th className="w-28 pb-2">Estado</th>
                    <th className="w-28 pb-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((metodo) => (
                    <tr key={metodo.id} className="border-t border-ink-100 align-top">
                      <td className="py-3">
                        <span className="rounded-control bg-ink-100 px-2 py-0.5 font-mono text-xs font-semibold text-ink-700">
                          {metodo.codigo}
                        </span>
                      </td>
                      <td className="py-3 text-ink-800">{metodo.descripcion}</td>
                      <td className="py-3">
                        <Badge variant={metodo.activo ? 'success' : 'neutral'}>
                          {metodo.activo ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Editar medio ${metodo.codigo}`}
                            title="Editar"
                            onClick={() => {
                              setErrorDialogo(null);
                              setDialogo({ abierto: true, metodo });
                            }}
                          >
                            <Pencil className="size-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={
                              metodo.activo ? `Desactivar medio ${metodo.codigo}` : `Activar medio ${metodo.codigo}`
                            }
                            title={metodo.activo ? 'Desactivar' : 'Activar'}
                            loading={mutaciones.cambiarEstado.isPending}
                            onClick={() => cambiarEstado(metodo)}
                          >
                            <Power
                              className={`size-4 ${metodo.activo ? 'text-emerald-600' : 'text-ink-400'}`}
                              aria-hidden="true"
                            />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {dialogo.abierto ? (
        <MetodoDialogo
          key={`medio-${dialogo.metodo?.id ?? 'nuevo'}`}
          metodo={dialogo.metodo}
          guardando={mutaciones.crear.isPending || mutaciones.actualizar.isPending}
          error={errorDialogo}
          onGuardar={guardar}
          onCerrar={() => setDialogo({ abierto: false, metodo: null })}
        />
      ) : null}
    </div>
  );
}
